// TMOS Public Website Product Page
// Date: January 27, 2025
// Purpose: Product information page with editable sections

import React, { useEffect, useState } from 'react';
import { PublicHeader } from '@/components/public/PublicHeader';
import publicApi from '@/services/publicApi';
import { useLanguage } from '@/state/language/LanguageProvider';

interface ProductSection {
    id: string;
    section_key: string;
    title?: string;
    content_html: string;
    display_order: number;
}

export const ProductPage: React.FC = () => {
    const { t } = useLanguage();
    const [sections, setSections] = useState<ProductSection[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadContent();
    }, []);

    const loadContent = async () => {
        try {
            const response = await publicApi.get('/public/product-content');
            if (response.data.success) {
                const sectionsData = response.data.data || [];
                setSections(sectionsData.sort((a: ProductSection, b: ProductSection) => a.display_order - b.display_order));
            }
        } catch (error) {
            console.error('Error loading product content:', error);
        } finally {
            setLoading(false);
        }
    };

    const getSectionTitle = (sectionKey: string) => {
        const titles: Record<string, string> = {
            overview: 'Overview',
            features: 'Features',
            security: 'Security',
            integrations: 'Integrations'
        };
        return titles[sectionKey] || sectionKey;
    };

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <PublicHeader />

            <main className="flex-1">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12">
                    {/* Page Header */}
                    <div className="text-center mb-12">
                        <h1 className="text-4xl font-normal text-gray-900 mb-4">
                            {t('website.product.title')}
                        </h1>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5d2e]"></div>
                            <span className="ml-4 text-gray-600">{t('website.product.loading')}</span>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            {sections.map((section) => (
                                <div key={section.id} className="bg-white rounded-2xl shadow-lg p-8">
                                    <h2 className="text-3xl font-normal text-gray-900 mb-6">
                                        {section.title || getSectionTitle(section.section_key)}
                                    </h2>
                                    <div
                                        className="prose prose-lg max-w-none"
                                        dangerouslySetInnerHTML={{ __html: section.content_html }}
                                    />
                                </div>
                            ))}

                            {sections.length === 0 && (
                                <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
                                    <p className="text-gray-500">{t('website.common.content_coming_soon')}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

        </div>
    );
};

